<template>
    <div class="flex flex-col gap-4">
        <label for="profile-name" class="flex flex-col gap-1.5">
            <span class="body-small-semibold uppercase text-valence-grey-750">Name</span>
            <input
                id="profile-name"
                v-model="profileForm.name"
                type="text"
                :maxlength="NAME_MAX_LENGTH"
                class="w-full rounded-xl border border-valence-grey-300 px-3 py-2 body text-valence-grey-900 focus:border-valence-primary-500 focus:outline-none focus:ring-2 focus:ring-valence-primary-500/20"
                :placeholder="hrisName ?? 'Your full name'" />
        </label>
        <label for="profile-title" class="flex flex-col gap-1.5">
            <span class="body-small-semibold uppercase text-valence-grey-750">Job Title</span>
            <input
                id="profile-title"
                v-model="profileForm.title"
                type="text"
                class="w-full rounded-xl border border-valence-grey-300 px-3 py-2 body text-valence-grey-900 focus:border-valence-primary-500 focus:outline-none focus:ring-2 focus:ring-valence-primary-500/20"
                :placeholder="hrisData?.job_title ?? 'Your job title'" />
        </label>
        <label for="profile-division" class="flex flex-col gap-1.5">
            <span class="body-small-semibold uppercase text-valence-grey-750">Team</span>
            <input
                id="profile-division"
                v-model="profileForm.division"
                type="text"
                class="w-full rounded-xl border border-valence-grey-300 px-3 py-2 body text-valence-grey-900 focus:border-valence-primary-500 focus:outline-none focus:ring-2 focus:ring-valence-primary-500/20"
                :placeholder="hrisData?.department ?? 'e.g. Sales, Engineering, Marketing'" />
        </label>
        <AccountSettingToggle id="profile-is-manager" v-model="profileForm.isManager" label="I lead a team" :bold="false" :compact="true" />
        <div class="flex items-center justify-end gap-3">
            <span v-if="saveError" class="body-small text-red-600">{{ saveError }}</span>
            <BaseButton theme="tertiary" size="sm" :disabled="isSaving" @click="emit('cancelled')"> Cancel </BaseButton>
            <BaseButton theme="secondary" size="sm" :disabled="isSaving" @click="handleSave">
                {{ isSaving ? "Saving..." : "Save" }}
            </BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { HrisData, UserBasic } from "~vue/appTypes";
import { type ExistingPublicProfile, type ProfilePayload } from "~vue/types/profile";
import AccountSettingToggle from "~vue/UserProfile/AccountSettingToggle.vue";
import { ACCESS } from "~vue/utils/constants";
import { computed, reactive, ref } from "vue";

import BaseButton from "./BaseButton.vue";

// first_name and last_name are each varchar(150) in the DB; the full name is
// split on the first space, so cap the input at 150 + 1 space + 150 (BUG-862).
const NAME_MAX_LENGTH = 301;

const props = withDefaults(
    defineProps<{
        existingPublicProfile?: ExistingPublicProfile | null;
        userBasic?: UserBasic;
        basicInfo?: { full_name: string } | null;
        hrisData?: HrisData | null;
        defaultProfileAccess?: string;
    }>(),
    {
        existingPublicProfile: null,
        userBasic: () => ({}) as UserBasic,
        basicInfo: null,
        hrisData: null,
        defaultProfileAccess: ACCESS.ORGANIZATION,
    },
);

const hrisName = computed(() => {
    const parts = [props.hrisData?.first_name, props.hrisData?.last_name].filter(Boolean);
    return parts.length ? parts.join(" ") : null;
});

const emit = defineEmits<{
    saved: [];
    cancelled: [];
}>();

interface ProfileForm {
    name: string;
    title: string;
    division: string;
    isManager: boolean;
}

const profileForm = reactive<ProfileForm>({
    name: props.existingPublicProfile?.name ?? props.basicInfo?.full_name ?? props.userBasic?.full_name ?? "",
    title: props.existingPublicProfile?.title ?? props.userBasic?.job_title ?? "",
    division: props.existingPublicProfile?.division ?? props.userBasic?.division ?? "",
    isManager: props.existingPublicProfile?.is_manager ?? props.userBasic?.is_manager ?? false,
});

const saveError = ref<string | null>(null);
const isSaving = ref(false);

const savePayload = ref<ProfilePayload | null>(null);
const { execute: executeSave, error: saveApiError } = useFetch("/api/accounts/profile/", { immediate: false }).post(savePayload).json();

async function handleSave(): Promise<void> {
    isSaving.value = true;
    saveError.value = null;
    savePayload.value = {
        name: profileForm.name,
        title: profileForm.title,
        division: profileForm.division,
        is_manager: profileForm.isManager,
        access: props.existingPublicProfile?.access ?? props.defaultProfileAccess,
    };

    await executeSave();
    isSaving.value = false;

    if (saveApiError.value) {
        saveError.value = "Failed to save. Please try again.";
    } else {
        emit("saved");
    }
}
</script>
